import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { examAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import dompurify from 'dompurify';
import { 
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiFilter, 
  FiUpload, FiEye, FiCheckCircle, FiUsers, FiDownload, FiFileText 
} from 'react-icons/fi';
import Select from 'react-select';

function decodeHtmlEntities(str) {
  if (!str || typeof str !== 'string') return '';
  const el = document.createElement('div');
  el.innerHTML = str;
  return el.textContent || el.innerText || '';
}

const QuestionBank = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showWordImportModal, setShowWordImportModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    tradegroup: '',
    trade: '',
    subject: '',
    chapter: '',
    topic: ''
  });
  
  // Temporary filters for the modal (won't trigger fetch until applied)
  const [tempFilters, setTempFilters] = useState({
    tradegroup: '',
    trade: '',
    subject: '',
    chapter: '',
    topic: ''
  });
  
  // Master data for filters
  const [tradegroups, setTradegroups] = useState([]);
  const [trades, setTrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  
  // Loading states for cascading dropdowns
  const [tradesLoading, setTradesLoading] = useState(false);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [topicsLoading, setTopicsLoading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Import
  const [importData, setImportData] = useState({
    tradegroup: '',
    trade: '',
    subject: '',
    chapter: '',
    topic: '',
    file: null
  });
  const [importing, setImporting] = useState(false);

  // Word import (separate from CSV)
  const [wordImportData, setWordImportData] = useState({
    tradegroup: '',
    trade: '',
    subject: '',
    chapter: '',
    topic: '',
    file: null
  });
  const [wordImporting, setWordImporting] = useState(false);

  useEffect(() => {
    fetchQuestions();
    fetchTradeGroups();
  }, [currentPage, filters]);

  // Dynamically load cascading data for tempFilters (modal)
  useEffect(() => {
    if (tempFilters.tradegroup) {
      fetchTrades(tempFilters.tradegroup);
    } else {
      setTrades([]);
    }
  }, [tempFilters.tradegroup]);

  useEffect(() => {
    if (tempFilters.tradegroup && tempFilters.trade) {
      fetchSubjects(tempFilters.tradegroup, tempFilters.trade);
    } else {
      setSubjects([]);
    }
  }, [tempFilters.tradegroup, tempFilters.trade]);

  useEffect(() => {
    if (tempFilters.tradegroup && tempFilters.trade && tempFilters.subject) {
      fetchChapters(tempFilters.tradegroup, tempFilters.trade, tempFilters.subject);
    } else {
      setChapters([]);
    }
  }, [tempFilters.tradegroup, tempFilters.trade, tempFilters.subject]);

  useEffect(() => {
    if (tempFilters.tradegroup && tempFilters.trade && tempFilters.subject && tempFilters.chapter) {
      fetchTopics(tempFilters.tradegroup, tempFilters.trade, tempFilters.subject, tempFilters.chapter);
    } else {
      setTopics([]);
    }
  }, [tempFilters.tradegroup, tempFilters.trade, tempFilters.subject, tempFilters.chapter]);

  // Dynamically load cascading data for import modal
  useEffect(() => {
    if (showImportModal && importData.tradegroup) {
      fetchTradesForImport(importData.tradegroup);
    }
  }, [showImportModal, importData.tradegroup]);

  useEffect(() => {
    if (showImportModal && importData.tradegroup && importData.trade) {
      fetchSubjectsForImport(importData.tradegroup, importData.trade);
    }
  }, [showImportModal, importData.tradegroup, importData.trade]);

  useEffect(() => {
    if (showImportModal && importData.tradegroup && importData.trade && importData.subject) {
      fetchChapters(importData.tradegroup, importData.trade, importData.subject);
    } else if (showImportModal) {
      setChapters([]);
    }
  }, [showImportModal, importData.tradegroup, importData.trade, importData.subject]);

  useEffect(() => {
    if (showImportModal && importData.tradegroup && importData.trade && importData.subject && importData.chapter) {
      fetchTopics(importData.tradegroup, importData.trade, importData.subject, importData.chapter);
    } else if (showImportModal) {
      setTopics([]);
    }
  }, [showImportModal, importData.tradegroup, importData.trade, importData.subject, importData.chapter]);

  // Load cascading data when Word import modal is open
  useEffect(() => {
    if (showWordImportModal && wordImportData.tradegroup) {
      fetchTradesForImport(wordImportData.tradegroup);
    } else if (showWordImportModal) {
      setTrades([]);
    }
  }, [showWordImportModal, wordImportData.tradegroup]);

  useEffect(() => {
    if (showWordImportModal && wordImportData.tradegroup && wordImportData.trade) {
      fetchSubjectsForImport(wordImportData.tradegroup, wordImportData.trade);
    } else if (showWordImportModal) {
      setSubjects([]);
    }
  }, [showWordImportModal, wordImportData.tradegroup, wordImportData.trade]);

  useEffect(() => {
    if (showWordImportModal && wordImportData.tradegroup && wordImportData.trade && wordImportData.subject) {
      fetchChapters(wordImportData.tradegroup, wordImportData.trade, wordImportData.subject);
    } else if (showWordImportModal) {
      setChapters([]);
    }
  }, [showWordImportModal, wordImportData.tradegroup, wordImportData.trade, wordImportData.subject]);

  useEffect(() => {
    if (showWordImportModal && wordImportData.tradegroup && wordImportData.trade && wordImportData.subject && wordImportData.chapter) {
      fetchTopics(wordImportData.tradegroup, wordImportData.trade, wordImportData.subject, wordImportData.chapter);
    } else if (showWordImportModal) {
      setTopics([]);
    }
  }, [showWordImportModal, wordImportData.tradegroup, wordImportData.trade, wordImportData.subject, wordImportData.chapter]);

  const fetchTradesForImport = async (tradegroupId) => {
    setTradesLoading(true);
    try {
      const response = await masterAPI.getTradesByGroup(tradegroupId);
      if (response.data.success) setTrades(response.data.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
    } finally {
      setTradesLoading(false);
    }
  };

  const fetchSubjectsForImport = async (tradegroup, trade) => {
    setSubjectsLoading(true);
    try {
      const response = await masterAPI.getSubjectsByTrade(tradegroup, trade);
      if (response.data.success) setSubjects(response.data.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        keywords: searchQuery,
        ...filters
      };
      
      const response = await examAPI.getQuestionBank(params);
      if (response.data.success) {
        setQuestions(response.data.data);
        setTotalPages(response.data.pagination?.last_page || 1);
      }
    } catch (error) {
      toast.error('Error fetching questions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTradeGroups = async () => {
    try {
      const response = await masterAPI.getTradeGroups();
      if (response.data.success) setTradegroups(response.data.data);
    } catch (error) {
      console.error('Error fetching trade groups:', error);
    }
  };

  const fetchTrades = async (tradegroupId) => {
    setTradesLoading(true);
    try {
      const response = await masterAPI.getTradesByGroup(tradegroupId);
      if (response.data.success) setTrades(response.data.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
    } finally {
      setTradesLoading(false);
    }
  };

  const fetchSubjects = async (tradegroup, trade) => {
    setSubjectsLoading(true);
    try {
      const response = await masterAPI.getSubjectsByTrade(tradegroup, trade);
      if (response.data.success) setSubjects(response.data.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const fetchChapters = async (tradegroup, trade, subject) => {
    setChaptersLoading(true);
    try {
      const response = await masterAPI.getChaptersBySubject(tradegroup, trade, subject);
      if (response.data.success) setChapters(response.data.data);
    } catch (error) {
      console.error('Error fetching chapters:', error);
    } finally {
      setChaptersLoading(false);
    }
  };

  const fetchTopics = async (tradegroup, trade, subject, chapter) => {
    setTopicsLoading(true);
    try {
      const response = await masterAPI.getTopicsByChapter(tradegroup, trade, subject, chapter);
      if (response.data.success) setTopics(response.data.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
    } finally {
      setTopicsLoading(false);
    }
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      padding: '2px 4px',
      borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
      '&:hover': { borderColor: '#9ca3af' },
      borderRadius: '0.5rem',
      minHeight: '42px'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 50
    })
  };

  // Transform data for react-select format
  const tradegroupOptions = useMemo(() => 
    tradegroups.map(tg => ({ value: tg.id, label: tg.name })),
    [tradegroups]
  );

  const tradeOptions = useMemo(() => 
    trades.map(t => ({ value: t.id, label: t.name })),
    [trades]
  );

  const subjectOptions = useMemo(() => 
    subjects.map(s => ({ value: s.id, label: s.name })),
    [subjects]
  );

  const chapterOptions = useMemo(() => 
    chapters.map(c => ({ value: c.id, label: c.name })),
    [chapters]
  );

  const topicOptions = useMemo(() => 
    topics.map(t => ({ value: t.id, label: t.name })),
    [topics]
  );

  const handleSearch = () => {
    setCurrentPage(1);
    fetchQuestions();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      const response = await examAPI.deleteQuestion(id);
      if (response.data.success) {
        toast.success('Question deleted successfully');
        fetchQuestions();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting question');
      console.error(error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.length === 0) {
      toast.warning('Please select questions to delete');
      return;
    }

    if (!window.confirm(`Delete ${selectedQuestions.length} selected question(s)?`)) {
      return;
    }

    try {
      const response = await examAPI.bulkDeleteQuestions(selectedQuestions);
      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedQuestions([]);
        fetchQuestions();
      }
    } catch (error) {
      toast.error('Error deleting questions');
      console.error(error);
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    
    if (!importData.file || !importData.tradegroup || !importData.trade) {
      toast.error('Please select file, category and trade');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importData.file);
      formData.append('fileupload', 'fileupload');
      formData.append('tradegroup', importData.tradegroup);
      formData.append('trade', importData.trade);
      formData.append('subject', importData.subject);
      formData.append('chapter', importData.chapter);
      formData.append('topic', importData.topic);

      const response = await examAPI.createQuestion(formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowImportModal(false);
        setImportData({ tradegroup: '', trade: '', subject: '', chapter: '', topic: '', file: null });
        fetchQuestions();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error importing questions');
    } finally {
      setImporting(false);
    }
  };

  const handleWordImportSubmit = async (e) => {
    e.preventDefault();
    if (!wordImportData.file || !wordImportData.tradegroup || !wordImportData.trade) {
      toast.error('Please select file, category and trade');
      return;
    }
    setWordImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', wordImportData.file);
      formData.append('wordupload', 'wordupload');
      formData.append('tradegroup', wordImportData.tradegroup);
      formData.append('trade', wordImportData.trade);
      formData.append('subject', wordImportData.subject);
      formData.append('chapter', wordImportData.chapter);
      formData.append('topic', wordImportData.topic);
      const response = await examAPI.createQuestion(formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowWordImportModal(false);
        setWordImportData({ tradegroup: '', trade: '', subject: '', chapter: '', topic: '', file: null });
        fetchQuestions();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error importing questions');
    } finally {
      setWordImporting(false);
    }
  };

  const handleFilterApply = () => {
    // Apply tempFilters to actual filters (this will trigger fetchQuestions via useEffect)
    setFilters({ ...tempFilters });
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const handleFilterReset = () => {
    const emptyFilters = {
      tradegroup: '',
      trade: '',
      subject: '',
      chapter: '',
      topic: ''
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const toggleSelectAll = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q.id));
    }
  };

  const toggleSelectQuestion = (id) => {
    setSelectedQuestions(prev =>
      prev.includes(id) ? prev.filter(qId => qId !== id) : [...prev, id]
    );
  };

  // No client-side filtering needed - data loaded dynamically from API

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  if (loading && questions.length === 0) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Question Bank</h1>
        <p className="text-gray-600">Manage your test questions and imports</p>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* Search */}
          <div className="flex-1 w-full md:w-auto">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 w-full md:w-auto">
            <button
              onClick={() => {
                setTempFilters({ ...filters }); // Initialize tempFilters with current filters
                setShowFilterModal(true);
              }}
              className="relative flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <FiFilter size={18} />
              Filter
              {activeFiltersCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <FiUpload size={18} />
              <span className="hidden sm:inline">Import CSV</span>
            </button>

            <button
              onClick={() => setShowWordImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <FiFileText size={18} />
              <span className="hidden sm:inline">Import Word</span>
            </button>
            
            <button
              onClick={() => navigate('/questions/add')}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              <FiPlus size={18} />
              <span className="hidden sm:inline">Add Question</span>
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedQuestions.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <span className="text-sm text-blue-900 font-medium">
              {selectedQuestions.length} question(s) selected
            </span>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
            >
              <FiTrash2 size={16} />
              Delete Selected
            </button>
          </div>
        )}
      </div>

      {/* Questions Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {questions.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-300 mb-4">
              <FiSearch size={64} className="mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No questions found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || activeFiltersCount > 0
                ? 'Try adjusting your search or filters'
                : 'Create your first question to get started'}
            </p>
            <button
              onClick={() => navigate('/questions/add')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              <FiPlus /> Add Question
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.length === questions.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Question</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Trade</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Answer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {questions.map((question) => (
                  console.log("question",question),
                  <tr key={question.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(question.id)}
                        onChange={() => toggleSelectQuestion(question.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 max-w-md truncate" title={decodeHtmlEntities(question.question)}>
                        {decodeHtmlEntities(question.question)}
                     
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{question.tradegroup_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{question.trade_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{question.subject_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                        {question.correct}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {question.is_shared == 1 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                          <FiUsers size={12} /> Shared
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                          <FiCheckCircle size={12} /> Own
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/questions/view/${question.id}`)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="View"
                        >
                          <FiEye size={16} />
                        </button>
                        {question.is_shared != 1 && (
                          <>
                            <button
                              onClick={() => navigate(`/questions/edit/${question.id}`)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="Edit"
                            >
                              <FiEdit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(question.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Filter Questions</h2>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <Select
                  value={tradegroupOptions.find(opt => opt.value === tempFilters.tradegroup) || null}
                  onChange={(selectedOption) => {
                    setTempFilters({ tradegroup: selectedOption?.value || '', trade: '', subject: '', chapter: '', topic: '' });
                  }}
                  options={tradegroupOptions}
                  placeholder="All Categories"
                  isClearable
                  isSearchable
                  styles={customSelectStyles}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trade
                  {tradesLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                </label>
                <Select
                  value={tradeOptions.find(opt => opt.value === tempFilters.trade) || null}
                  onChange={(selectedOption) => {
                    setTempFilters({ ...tempFilters, trade: selectedOption?.value || '', subject: '', chapter: '', topic: '' });
                  }}
                  options={tradeOptions}
                  placeholder={tradesLoading ? "Loading trades..." : "All Trades"}
                  isClearable
                  isSearchable
                  isDisabled={!tempFilters.tradegroup || tradesLoading}
                  isLoading={tradesLoading}
                  styles={customSelectStyles}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                  {subjectsLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                </label>
                <Select
                  value={subjectOptions.find(opt => opt.value === tempFilters.subject) || null}
                  onChange={(selectedOption) => {
                    setTempFilters({ ...tempFilters, subject: selectedOption?.value || '', chapter: '', topic: '' });
                  }}
                  options={subjectOptions}
                  placeholder={subjectsLoading ? "Loading subjects..." : "All Subjects"}
                  isClearable
                  isSearchable
                  isDisabled={!tempFilters.trade || subjectsLoading}
                  isLoading={subjectsLoading}
                  styles={customSelectStyles}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chapter (Optional)
                  {chaptersLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                </label>
                <Select
                  value={chapterOptions.find(opt => opt.value === tempFilters.chapter) || null}
                  onChange={(selectedOption) => {
                    setTempFilters({ ...tempFilters, chapter: selectedOption?.value || '', topic: '' });
                  }}
                  options={chapterOptions}
                  placeholder={chaptersLoading ? "Loading chapters..." : "All Chapters"}
                  isClearable
                  isSearchable
                  isDisabled={!tempFilters.subject || chaptersLoading}
                  isLoading={chaptersLoading}
                  styles={customSelectStyles}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Topic (Optional)
                  {topicsLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                </label>
                <Select
                  value={topicOptions.find(opt => opt.value === tempFilters.topic) || null}
                  onChange={(selectedOption) => {
                    setTempFilters({ ...tempFilters, topic: selectedOption?.value || '' });
                  }}
                  options={topicOptions}
                  placeholder={topicsLoading ? "Loading topics..." : "All Topics"}
                  isClearable
                  isSearchable
                  isDisabled={!tempFilters.chapter || topicsLoading}
                  isLoading={topicsLoading}
                  styles={customSelectStyles}
                />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleFilterReset}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleFilterApply}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 max-h-[100vh] overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Import Questions from CSV</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleImportSubmit} className="p-6">
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-blue-900">
                    <strong>CSV Format:</strong> Question, Option A, Option B, Option C, Option D, Option E, Answer (a/b/c/d/e), Explanation
                  </p>
                  <a
                    href="/sample_questions.csv"
                    download
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <FiDownload size={14} />
                    Download Sample
                  </a>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-600">*</span>
                  </label>
                  <Select
                    value={tradegroupOptions.find(opt => opt.value === importData.tradegroup) || null}
                    onChange={(selectedOption) => {
                      setImportData({ tradegroup: selectedOption?.value || '', trade: '', subject: '', chapter: '', topic: '', csv_file: importData.csv_file });
                    }}
                    options={tradegroupOptions}
                    placeholder="Select Category"
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trade <span className="text-red-600">*</span>
                    {tradesLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                  </label>
                  <Select
                    value={tradeOptions.find(opt => opt.value === importData.trade) || null}
                    onChange={(selectedOption) => {
                      setImportData({ ...importData, trade: selectedOption?.value || '', subject: '', chapter: '', topic: '' });
                    }}
                    options={tradeOptions}
                    placeholder={tradesLoading ? "Loading trades..." : "Select Trade"}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    isDisabled={!importData.tradegroup || tradesLoading}
                    isLoading={tradesLoading}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject (Optional)
                    {subjectsLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                  </label>
                  <Select
                    value={subjectOptions.find(opt => opt.value === importData.subject) || null}
                    onChange={(selectedOption) => {
                      setImportData({ ...importData, subject: selectedOption?.value || '', chapter: '', topic: '' });
                    }}
                    options={subjectOptions}
                    placeholder={subjectsLoading ? "Loading subjects..." : "Select Subject"}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    isDisabled={!importData.tradegroup || !importData.trade || subjectsLoading}
                    isLoading={subjectsLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chapter (Optional)
                    {chaptersLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                  </label>
                  <Select
                    value={chapterOptions.find(opt => opt.value === importData.chapter) || null}
                    onChange={(selectedOption) => {
                      setImportData({ ...importData, chapter: selectedOption?.value || '', topic: '' });
                    }}
                    options={chapterOptions}
                    placeholder={chaptersLoading ? "Loading chapters..." : "Select Chapter"}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    isDisabled={!importData.subject || chaptersLoading}
                    isLoading={chaptersLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic (Optional)
                    {topicsLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                  </label>
                  <Select
                    value={topicOptions.find(opt => opt.value === importData.topic) || null}
                    onChange={(selectedOption) => {
                      setImportData({ ...importData, topic: selectedOption?.value || '' });
                    }}
                    options={topicOptions}
                    placeholder={topicsLoading ? "Loading topics..." : "Select Topic"}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    isDisabled={!importData.chapter || topicsLoading}
                    isLoading={topicsLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CSV File <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportData({ ...importData, file: e.target.files[0] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  disabled={importing}
                >
                  {importing ? 'Importing...' : 'Import Questions'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Word Import Modal */}
      {showWordImportModal && (
        <div className="fixed max-h-[100vh] overflow-y-auto inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Import Questions from Word</h2>
              <button
                onClick={() => setShowWordImportModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleWordImportSubmit} className="p-6">
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-sm text-blue-900">
                    <strong>Word format:</strong> Use a .docx file with one table. First row = header. Columns: Question, Option A, Option B, Option C, Option D, Option E, Answer (a/b/c/d/e), Explanation.
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const res = await examAPI.getSampleWordFile();
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a');
                        a.href = url;
                        a.setAttribute('download', 'sample_questions.docx');
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('Sample file downloaded');
                      } catch (e) {
                        toast.error('Could not download sample');
                      }
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shrink-0"
                  >
                    <FiDownload size={14} />
                    Download Sample
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-600">*</span>
                  </label>
                  <Select
                    value={tradegroupOptions.find(opt => opt.value === wordImportData.tradegroup) || null}
                    onChange={(selectedOption) => {
                      setWordImportData({ tradegroup: selectedOption?.value || '', trade: '', subject: '', chapter: '', topic: '', file: wordImportData.file });
                    }}
                    options={tradegroupOptions}
                    placeholder="Select Category"
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trade <span className="text-red-600">*</span>
                    {tradesLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                  </label>
                  <Select
                    value={tradeOptions.find(opt => opt.value === wordImportData.trade) || null}
                    onChange={(selectedOption) => {
                      setWordImportData({ ...wordImportData, trade: selectedOption?.value || '', subject: '', chapter: '', topic: '' });
                    }}
                    options={tradeOptions}
                    placeholder={tradesLoading ? "Loading trades..." : "Select Trade"}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    isDisabled={!wordImportData.tradegroup || tradesLoading}
                    isLoading={tradesLoading}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject (Optional)
                    {subjectsLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                  </label>
                  <Select
                    value={subjectOptions.find(opt => opt.value === wordImportData.subject) || null}
                    onChange={(selectedOption) => {
                      setWordImportData({ ...wordImportData, subject: selectedOption?.value || '', chapter: '', topic: '' });
                    }}
                    options={subjectOptions}
                    placeholder={subjectsLoading ? "Loading subjects..." : "Select Subject"}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    isDisabled={!wordImportData.tradegroup || !wordImportData.trade || subjectsLoading}
                    isLoading={subjectsLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chapter (Optional)
                    {chaptersLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                  </label>
                  <Select
                    value={chapterOptions.find(opt => opt.value === wordImportData.chapter) || null}
                    onChange={(selectedOption) => {
                      setWordImportData({ ...wordImportData, chapter: selectedOption?.value || '', topic: '' });
                    }}
                    options={chapterOptions}
                    placeholder={chaptersLoading ? "Loading chapters..." : "Select Chapter"}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    isDisabled={!wordImportData.subject || chaptersLoading}
                    isLoading={chaptersLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic (Optional)
                    {topicsLoading && <span className="ml-2 text-xs text-primary-600">Loading...</span>}
                  </label>
                  <Select
                    value={topicOptions.find(opt => opt.value === wordImportData.topic) || null}
                    onChange={(selectedOption) => {
                      setWordImportData({ ...wordImportData, topic: selectedOption?.value || '' });
                    }}
                    options={topicOptions}
                    placeholder={topicsLoading ? "Loading topics..." : "Select Topic"}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    isDisabled={!wordImportData.chapter || topicsLoading}
                    isLoading={topicsLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Word File (.docx) <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".docx"
                    onChange={(e) => setWordImportData({ ...wordImportData, file: e.target.files[0] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowWordImportModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={wordImporting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  disabled={wordImporting}
                >
                  {wordImporting ? 'Importing...' : 'Import from Word'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBank;
